import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, ClipboardCheck, CalendarClock, FileText, Loader2, Lock, Printer, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import api, { formatApiError, openProtectedFile } from "@/lib/api";
import AdminEvalManager from "@/components/AdminEvalManager";

const SCORES = [1, 2, 3, 4, 5];
const EV_STATUS = {
  pending: ["بانتظار المراجعة", "bg-amber-100 text-amber-800 border-amber-300"],
  accepted: ["مقبول", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  rejected: ["مرفوض", "bg-rose-100 text-rose-700 border-rose-300"],
};

const round2 = (n) => Math.round((n ?? 0) * 100) / 100;
const liveTotals = (criteria, scores) => {
  const wsum = criteria.reduce((a, c) => a + c.weight, 0) || 1;
  const t5 = criteria.reduce((a, c, i) => a + (Number(scores[String(i + 1)]) || 0) * c.weight, 0) / wsum;
  return { total: round2(t5), pct: Math.round(t5 / 5 * 1000) / 10 };
};

export default function PerformanceTab() {
  const [meta, setMeta] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [period, setPeriod] = useState("الفصل الأول");
  const [job, setJob] = useState("معلم");
  const [empId, setEmpId] = useState(null);
  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [evDetail, setEvDetail] = useState(null);
  const [evOpen, setEvOpen] = useState(null);
  const [overview, setOverview] = useState({});
  const [pending, setPending] = useState({ by_job: {}, by_employee: {} });
  const loadPending = useCallback(async (p) => {
    try {
      const r = await api.get("/evidence/pending-counts", { params: { period: p } });
      setPending(r.data);
    } catch { /* keep stale counts */ }
  }, []);
  const [rejectNote, setRejectNote] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingEval, setLoadingEval] = useState(false);
  const [evalStart, setEvalStart] = useState(undefined);
  const [savingEvalStart, setSavingEvalStart] = useState(false);

  const saveEvalStart = async () => {
    setSavingEvalStart(true);
    try {
      const { data: cur } = await api.get("/settings");
      await api.put("/settings", { ...cur, eval_start_date: evalStart || null });
      toast.success("تم حفظ تاريخ بداية احتساب الأداء الوظيفي — سيُطبق على الدرجات فوراً");
    } catch { toast.error("فشل الحفظ"); }
    finally { setSavingEvalStart(false); }
  };

  useEffect(() => {
    api.get("/evaluations/meta").then((m) => setMeta(m.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "تعذر التحميل"));
    api.get("/settings").then((r) => setEvalStart(r.data.eval_start_date || "")).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/evaluations/employees", { params: { period } })
      .then((e) => setEmployees(e.data)).catch(() => {});
    loadPending(period);
    const t = setInterval(() => loadPending(period), 30000);
    return () => clearInterval(t);
  }, [period, loadPending]);

  useEffect(() => {
    if (job !== "معلم") { setOverview({}); return; }
    api.get("/evidence/overview", { params: { period } })
      .then((r) => setOverview(Object.fromEntries(r.data.map((o) => [o.employee_id, o]))))
      .catch(() => {});
  }, [job, period]);

  const [adminOverview, setAdminOverview] = useState({});
  useEffect(() => {
    if (job !== "إداري") { setAdminOverview({}); return; }
    api.get("/admin-eval/overview", { params: { period } })
      .then((r) => setAdminOverview(Object.fromEntries(r.data.map((o) => [o.employee_id, o]))))
      .catch(() => {});
  }, [job, period]);

  const loadEval = useCallback(async (id, p) => {
    setLoadingEval(true);
    try {
      const r = await api.get(`/evaluations/${id}`, { params: { period: p } });
      setData(r.data);
      setScores({ ...(r.data.manager?.scores || {}), ...(r.data.evidence_scores || {}) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setData(null);
    } finally { setLoadingEval(false); }
  }, []);

  const loadEvidence = useCallback(async (id, p) => {
    try {
      const r = await api.get(`/evidence/${id}`, { params: { period: p } });
      setEvDetail(r.data);
    } catch { setEvDetail(null); }
  }, []);

  const pickEmployee = (id) => {
    setEmpId(id); setEvDetail(null); setEvOpen(null);
    if (job === "إداري") { setData(null); return; }
    loadEval(id, period);
  };

  const changePeriod = (p) => {
    setPeriod(p); setEvDetail(null); setEvOpen(null);
    if (empId && job !== "إداري") loadEval(empId, p);
  };

  const isEvidence = (key) => data?.evidence_scores && key in data.evidence_scores;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/evaluations/${empId}`, { period, scores });
      setData((d) => ({ ...d, manager: r.data }));
      setScores({ ...r.data.scores });
      toast.success(`تم حفظ التقييم — النتيجة ${r.data.pct}٪`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const review = async (evId, action) => {
    try {
      await api.post("/evidence/review", { evidence_id: evId, action, note: rejectNote[evId] || "" });
      toast.success(action === "accept" ? "تم قبول الشاهد وحُدّث التقييم" : "تم رفض الشاهد");
      await Promise.all([loadEval(empId, period), loadEvidence(empId, period), loadPending(period)]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const totals = useMemo(
    () => (data ? liveTotals(data.form.criteria, scores) : null), [data, scores]);

  const printCompare = async () => {
    const b = await api.get("/branding").then((r) => r.data).catch(() => ({}));
    const mTotals = totals;
    const sTotals = data.self ? { total: data.self.total, pct: data.self.pct } : null;
    const rows = data.form.criteria.map((c, i) => {
      const k = String(i + 1);
      const s = data.self?.scores?.[k];
      const m = scores[k];
      const diff = s != null && m != null ? round2(m - s) : null;
      return `<tr><td>${i + 1}</td><td style="text-align:right">${c.name}</td><td>${c.weight}٪</td>` +
        `<td>${s ?? "—"}</td><td>${m ?? "—"}</td>` +
        `<td>${diff != null ? (diff > 0 ? `+${diff}` : diff) : "—"}</td></tr>`;
    }).join("");
    const logoRow = `
      ${b.logo_school ? `<img src="${b.logo_school}" style="height:70px">` : "<span></span>"}
      <div><b>${b.line1 || "المملكة العربية السعودية"}</b><br><b>${b.line2 || "وزارة التعليم"}</b>${b.line3 ? `<br><b>${b.line3}</b>` : ""}</div>
      ${b.logo_ministry ? `<img src="${b.logo_ministry}" style="height:80px">` : "<span></span>"}`;
    const sig = `
      <div style="margin-top:34px;font-weight:700">مدير المدرسة: ${b.manager_name || "____________________"}</div>
      <div style="margin-top:10px;display:flex;gap:24px;align-items:center">
        ${b.signature ? `<img src="${b.signature}" style="height:44px">` : "<span>التوقيع: ____________</span>"}
        ${b.stamp ? `<img src="${b.stamp}" style="height:70px">` : ""}
      </div>`;
    const w = window.open("", "_blank");
    w.document.write(`<html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>تقرير مقارنة تقييم الأداء</title>
      <style>body{font-family:'Traditional Arabic','Amiri',serif;padding:24px;color:#0f172a}
      .hdr{display:flex;justify-content:space-between;align-items:center;text-align:center;border-bottom:3px double #064e3b;padding-bottom:10px}
      h2{color:#064e3b;margin:14px 0 4px} p.sub{color:#475569;margin:0 0 12px;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:12.5px} th,td{border:1px solid #334155;padding:5px 6px;text-align:center}
      th{background:#064e3b;color:#fff} .tot td{font-weight:800;background:#f1f5f9}</style></head><body>
      <div class="hdr">${logoRow}</div>
      <h2>تقرير مقارنة تقييم الأداء الوظيفي</h2>
      <p class="sub">الموظف: ${data.employee.name} — ${data.employee.job} | ${data.form.title} | الفترة: ${period} — العام الدراسي ${data.year}</p>
      <table><thead><tr><th>م</th><th>المعيار</th><th>الوزن</th><th>التقييم الذاتي</th><th>تقييم المدير</th><th>الفرق</th></tr></thead>
      <tbody>${rows}
      <tr class="tot"><td colspan="3">النتيجة النهائية (من 5)</td><td>${sTotals ? sTotals.total : "—"}</td><td>${mTotals.total}</td>
        <td>${sTotals ? (mTotals.total - sTotals.total > 0 ? "+" : "") + round2(mTotals.total - sTotals.total) : "—"}</td></tr>
      <tr class="tot"><td colspan="3">النسبة المئوية</td><td>${sTotals ? sTotals.pct + "٪" : "—"}</td><td>${mTotals.pct}٪</td>
        <td>${sTotals ? (mTotals.pct - sTotals.pct > 0 ? "+" : "") + round2(mTotals.pct - sTotals.pct) + "٪" : "—"}</td></tr>
      </tbody></table>${sig}</body></html>`);
    w.document.close();
    // الصور base64 لكنها تُفكّ بشكل غير متزامن — ننتظر اكتمالها قبل الطباعة
    const pImgs = [...w.document.querySelectorAll("img")];
    Promise.all(pImgs.map((img) => (img.complete && img.naturalWidth > 0)
      ? Promise.resolve()
      : new Promise((res) => {
          img.onload = () => res();
          img.onerror = () => res();
          if (img.decode) img.decode().then(res).catch(() => {});
          setTimeout(res, 4000);
        }))).then(() => { w.focus(); w.print(); });
  };

  if (!meta || employees === null)
    return <div className="bg-white rounded-xl border p-10 text-center text-slate-400 text-sm">جارٍ التحميل...</div>;

  const jobEmps = employees.filter((e) => e.job === job)
    .sort((a, b) => (b.bonus || 0) - (a.bonus || 0));

  return (
    <div className="space-y-4" data-testid="performance-tab">
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3" data-testid="eval-start-date-card">
        <CalendarClock className="w-4 h-4 text-emerald-800 shrink-0" />
        <div className="space-y-0.5">
          <p className="text-xs font-bold text-slate-600">تاريخ بداية احتساب الأداء الوظيفي</p>
          <p className="text-[10px] text-slate-400 leading-4">
            يبدأ احتساب درجات التقييم (الحضور والانصراف والمساءلات) من هذا التاريخ فقط ويُستبعد ما قبله — اتركه فارغاً لاحتساب الفترة كاملة
          </p>
        </div>
        <div className="flex items-center gap-2 mr-auto">
          <DateInput value={evalStart ?? ""} data-testid="setting-eval-start-date"
            onChange={(e) => setEvalStart(e.target.value)}
            className="h-9 w-44" />
          <Button size="sm" onClick={saveEvalStart} disabled={savingEvalStart || evalStart === undefined}
            data-testid="btn-save-eval-start"
            className="h-9 text-xs gap-1 bg-emerald-900 hover:bg-emerald-950 text-white">
            <Save className="w-3.5 h-3.5" /> {savingEvalStart ? "جارٍ الحفظ..." : "حفظ"}
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-500">الفترة:</span>
        {meta.periods.map((p) => (
          <button key={p} onClick={() => changePeriod(p)} data-testid={`eval-period-${p}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors ${
              period === p ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            {p}
          </button>
        ))}
        <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1"
          data-testid="eval-year">العام الدراسي {meta.year}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="eval-jobs">
        {meta.jobs.map((j) => (
          <button key={j} onClick={() => { setJob(j); setEmpId(null); setData(null); }}
            data-testid={`eval-job-${j}`}
            className={`relative rounded-xl border-2 p-3 text-center transition-colors ${
              job === j ? "border-emerald-800 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            {pending.by_job[j] > 0 && (
              <span data-testid={`eval-job-pending-${j}`}
                className="absolute -top-1.5 -left-1.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[10px] font-extrabold flex items-center justify-center shadow">
                {pending.by_job[j]}
              </span>
            )}
            <ClipboardCheck className={`w-5 h-5 mx-auto mb-1 ${job === j ? "text-emerald-800" : "text-slate-400"}`} />
            <p className="text-xs font-bold">{j}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{employees.filter((e) => e.job === j).length} موظف</p>
          </button>
        ))}
        <button onClick={() => { setJob("إداري"); setEmpId(null); setData(null); }}
          data-testid="eval-job-إداري"
          className={`relative rounded-xl border-2 p-3 text-center transition-colors ${
            job === "إداري" ? "border-emerald-800 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
          {pending.by_job["إداري"] > 0 && (
            <span data-testid="eval-job-pending-إداري"
              className="absolute -top-1.5 -left-1.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[10px] font-extrabold flex items-center justify-center shadow">
              {pending.by_job["إداري"]}
            </span>
          )}
          <ClipboardCheck className={`w-5 h-5 mx-auto mb-1 ${job === "إداري" ? "text-emerald-800" : "text-slate-400"}`} />
          <p className="text-xs font-bold">إداري</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{employees.filter((e) => e.job === "إداري").length} موظف</p>
        </button>
      </div>

      <div className="bg-white rounded-xl border p-4">
        <p className="text-xs font-bold text-slate-500 mb-2">موظفو فئة «{job}»:</p>
        {jobEmps.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-6" data-testid="eval-empty-job">لا يوجد موظفون بهذا المسمى</p>)}
        <div className="flex flex-wrap gap-2" data-testid="eval-employees">
          {jobEmps.map((e) => (
            <button key={e.id} onClick={() => pickEmployee(e.id)} data-testid={`eval-emp-${e.id}`}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors flex items-center gap-2 ${
                empId === e.id ? "bg-emerald-900 text-white border-emerald-900" : "bg-white hover:bg-slate-50"}`}>
              {e.name}
              {(pending.by_employee[e.id]?.total || 0) > 0 && (
                <span data-testid={`eval-emp-pending-${e.id}`}
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-extrabold flex items-center justify-center shadow-sm">
                  {pending.by_employee[e.id].total}
                </span>
              )}
              {job === "معلم" && (e.bonus || 0) > 0 && (
                <span data-testid={`eval-emp-bonus-${e.id}`}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold border ${
                    empId === e.id ? "bg-amber-400 text-amber-950 border-amber-400"
                                   : "bg-amber-100 text-amber-800 border-amber-300"}`}
                  title="نقاط المفاضلة — شواهد إضافية مقبولة فوق المطلوب">
                  مفاضلة +{e.bonus}
                </span>
              )}
              {overview[e.id] && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${empId === e.id ? "bg-emerald-800" : "bg-sky-50 text-sky-800 border border-sky-200"}`}
                  data-testid={`eval-emp-progress-${e.id}`}>شواهد {overview[e.id].completion_pct}٪</span>
              )}
              {adminOverview[e.id] && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${empId === e.id ? "bg-emerald-800" : "bg-amber-50 text-amber-800 border border-amber-200"}`}
                  data-testid={`eval-emp-goals-${e.id}`}>
                  {adminOverview[e.id].goals_count ? `أهداف ${adminOverview[e.id].goals_count}` : "لم يختر أهدافه"}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loadingEval && <div className="bg-white rounded-xl border p-10 text-center text-slate-400 text-sm">جارٍ التحميل...</div>}

      {empId && job === "إداري" && <AdminEvalManager empId={empId} period={period} onReviewed={() => loadPending(period)} />}

      {data && !loadingEval && job !== "إداري" && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="eval-form">
          <div className="bg-emerald-950 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <p className="font-extrabold text-sm" data-testid="eval-form-title">
              {data.form.title} — {data.employee.name}
            </p>
            <p className="text-[11px] text-emerald-300/80">{period} — {data.year}</p>
          </div>
          <div className="divide-y">
            {data.form.criteria.map((c, i) => {
              const k = String(i + 1);
              const locked = isEvidence(k);
              const evCrit = evDetail?.criteria?.find((x) => x.criterion === i + 1);
              return (
                <div key={k} className="px-4 py-3" data-testid={`eval-criterion-${k}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <p className="flex-1 min-w-[200px] text-[13px] font-semibold leading-6">{c.name}</p>
                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{c.weight}٪</span>
                    {(pending.by_employee[empId]?.by_criterion?.[k] || 0) > 0 && (
                      <span data-testid={`eval-crit-pending-${k}`}
                        className="h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-extrabold flex items-center justify-center">
                        {pending.by_employee[empId].by_criterion[k]} جديد
                      </span>
                    )}
                    {locked ? (
                      <span className="flex items-center gap-1.5 text-xs font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
                        data-testid={`eval-auto-score-${k}`}>
                        <Lock className="w-3 h-3" /> تلقائي من الشواهد: {scores[k] ?? 0}
                      </span>
                    ) : (
                      <div className="flex gap-1" data-testid={`eval-score-${k}`}>
                        {SCORES.map((s) => (
                          <button key={s} onClick={() => setScores({ ...scores, [k]: s })}
                            data-testid={`eval-score-${k}-${s}`}
                            className={`w-8 h-8 rounded-lg border text-xs font-extrabold transition-colors ${
                              Number(scores[k]) === s ? "bg-emerald-900 text-white border-emerald-900" : "bg-white hover:bg-emerald-50"}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                    {locked && (
                      <button onClick={() => {
                          const next = evOpen === i + 1 ? null : i + 1;
                          setEvOpen(next);
                          if (next && !evDetail) loadEvidence(empId, period);
                        }}
                        data-testid={`eval-evidence-toggle-${k}`}
                        className="flex items-center gap-1 text-[11px] font-bold text-emerald-800 hover:underline">
                        <FileText className="w-3.5 h-3.5" /> الشواهد
                        {evOpen === i + 1 ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                  {locked && evOpen === i + 1 && (
                    <div className="mt-2 mr-9 space-y-1.5" data-testid={`eval-evidence-list-${k}`}>
                      {i === 0 && (data.criterion1?.items || []).map((it) => (
                        <div key={it.name}
                          className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/50 px-3 py-2"
                          data-testid={`evidence-auto-item-${it.name}`}>
                          <p className="flex-1 min-w-[180px] text-[11px] font-semibold text-slate-700">{it.name}</p>
                          <span className="text-[10px] font-bold text-sky-800 bg-sky-100 border border-sky-300 rounded-full px-2 py-0.5">آلي: {it.score}/5</span>
                          <span className="text-[10px] text-slate-400">{it.basis}</span>
                        </div>
                      ))}
                      {!evDetail && <p className="text-[11px] text-slate-400">جارٍ تحميل الشواهد...</p>}
                      {evCrit?.items.map((it) => (
                        <div key={it.idx} className="rounded-lg border bg-slate-50/60 px-3 py-2 space-y-1.5"
                          data-testid={`evidence-item-${k}-${it.idx}`}>
                          <p className="text-[11px] font-semibold text-slate-700">
                            {it.name}
                            {(it.evidence || []).length > 0 &&
                              <span className="text-slate-400 font-normal"> ({it.evidence.length} ملف)</span>}
                          </p>
                          {(it.evidence || []).length === 0 && (
                            <span className="text-[10px] text-slate-400">لم يُرفع بعد</span>
                          )}
                          {(it.evidence || []).map((ev) => {
                            const st = EV_STATUS[ev.status];
                            return (
                              <div key={ev.id} className="flex flex-wrap items-center gap-2 border-t border-slate-200/70 pt-1.5"
                                data-testid={`evidence-file-${ev.id}`}>
                                <p className="flex-1 min-w-[180px] text-[10px] text-slate-500 truncate" dir="auto">
                                  {ev.original_name || "شاهد مرفوع"}</p>
                                <button type="button" onClick={() => openProtectedFile(`/evidence/file/${ev.id}`)}
                                  data-testid={`evidence-view-${ev.id}`}
                                  className="text-[11px] font-bold text-sky-700 hover:underline">عرض الملف</button>
                                <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${st[1]}`}
                                  data-testid={`evidence-status-${ev.id}`}>{st[0]}</span>
                                {ev.status !== "accepted" && (
                                  <button onClick={() => review(ev.id, "accept")} data-testid={`evidence-accept-${ev.id}`}
                                    className="flex items-center gap-0.5 text-[11px] font-bold text-emerald-700 hover:text-emerald-900">
                                    <Check className="w-3.5 h-3.5" /> قبول
                                  </button>
                                )}
                                {ev.status !== "rejected" && (
                                  <button onClick={() => review(ev.id, "reject")} data-testid={`evidence-reject-${ev.id}`}
                                    className="flex items-center gap-0.5 text-[11px] font-bold text-rose-600 hover:text-rose-800">
                                    <X className="w-3.5 h-3.5" /> رفض
                                  </button>
                                )}
                                {ev.status === "pending" && (
                                  <input value={rejectNote[ev.id] || ""} placeholder="ملاحظة الرفض (اختياري)"
                                    data-testid={`evidence-note-${ev.id}`}
                                    onChange={(e) => setRejectNote({ ...rejectNote, [ev.id]: e.target.value })}
                                    className="h-7 w-36 rounded-md border px-2 text-[11px]" />
                                )}
                                {ev.status === "rejected" && ev.note && (
                                  <span className="text-[10px] text-rose-600">ملاحظة: {ev.note}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="border-t bg-slate-50/60 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="eval-totals">
            <span className="text-xs font-bold text-emerald-900 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1"
              data-testid="eval-manager-total">تقييم المدير: {totals.total} / 5 ({totals.pct}٪)</span>
            {job === "معلم" && (
              <span className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-3 py-1"
                data-testid="eval-bonus-points"
                title="شواهد إضافية مقبولة فوق المطلوب — تُستخدم للمفاضلة بين المعلمين">
                نقاط المفاضلة: +{data.bonus_points ?? 0}
              </span>
            )}
            <span className="text-xs font-bold text-sky-900 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
              data-testid="eval-self-total">
              التقييم الذاتي: {data.self ? `${data.self.total} / 5 (${data.self.pct}٪)` : "لم يُعبَّأ بعد"}
            </span>
            {data.self && (
              <span className="text-xs font-bold text-slate-700 bg-white border rounded-full px-3 py-1"
                data-testid="eval-diff">الفرق: {round2(totals.pct - data.self.pct)}٪</span>
            )}
            <div className="mr-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={printCompare} data-testid="eval-print-btn"
                className="h-9 text-xs gap-1.5">
                <Printer className="w-4 h-4" /> تقرير المقارنة
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="eval-save-btn"
                className="h-9 text-xs gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ التقييم
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
